import { Loader } from "rsuite";
import FullCalendar, { EventClickArg } from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import bootstrapPlugin from "@fullcalendar/bootstrap";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import { useMemo, useState, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../core/redux/ReduxStore";
import { ActionAppointmentCalendar } from "../../core/redux/slices/sliceAppointmentCalendar";
import { ApiSchemaAppointmentByMonth } from "../../models/api/appointment";
import { UtilityDate, Dashboard, IEventData } from "../../utils";
import { ModalEditCheckInCheckOut } from "../../components/modal";
import { useHistory } from "react-router-dom";
import { AppRouteUi } from "../../config/appRoute";
import { ListViewEvent, MonthlyViewEvent } from "./event";

export const AppointmentCalender = ({
  data,
  isValidating,
}: {
  data: ApiSchemaAppointmentByMonth[] | undefined;
  isValidating?: boolean;
}) => {
  const EVENT_LIMIT = 3;
  const history = useHistory();
  const calendarRef = useRef<FullCalendar | null>(null);
  const [showCheckInOutModal, setCheckInOutModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState({
    appointmentId: "",
    date: "",
    start: "",
    end: "",
  });
  const {
    calendar: { monthStart, monthEnd },
    userRole,
  } = useAppSelector((state) => ({
    calendar: state.appointmentCalendar,
    userRole: state.auth.userRole,
  }));
  const dispatch = useAppDispatch();

  const [currentView, setCurrentView] = useState("dayGridMonth");

  const limitEventsPerDay = (events: any[], isMonthView: boolean) => {
    if (!isMonthView) return events;

    const eventsByDay: { [key: string]: any[] } = {};

    events.forEach((event) => {
      const date = new Date(event.start).toDateString();
      if (!eventsByDay[date]) {
        eventsByDay[date] = [];
      }
      eventsByDay[date].push(event);
    });

    // Sort events in each day by start time
    Object.keys(eventsByDay).forEach((date) => {
      eventsByDay[date].sort(
        (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
      );
    });

    return Object.values(eventsByDay).reduce((acc, dayEvents) => {
      if (dayEvents.length <= EVENT_LIMIT) return [...acc, ...dayEvents];

      const visibleEvents = dayEvents.slice(0, EVENT_LIMIT);
      const remainingCount = dayEvents.length - EVENT_LIMIT;

      // Add remaining count only to the last visible event
      visibleEvents[EVENT_LIMIT - 1] = {
        ...visibleEvents[EVENT_LIMIT - 1],
        extendedProps: {
          ...visibleEvents[EVENT_LIMIT - 1].extendedProps,
          remainingCount,
          isLastEvent: true,
        },
      };

      return [...acc, ...visibleEvents];
    }, []);
  };

  const events = useMemo(() => {
    if (!data?.length) return [];
    const allEvents = Dashboard.generateEvent({ monthStart, monthEnd, data });
    return limitEventsPerDay(allEvents, currentView === "dayGridMonth");
  }, [data, monthStart, monthEnd, currentView]);

  const { isLoading } = useAppSelector((state) => state.appointmentCalendar);
  const updateCheckInOut = () => {};
  const toggleCheckInOutModal = () => {
    setCheckInOutModal((prev) => !prev);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  useEffect(() => {
    window.addEventListener("resize", () => {
      setIsMobile(window.innerWidth < 1200);
    });
  }, []);

  return (
    <div className="disabled">
      {(isValidating || isLoading) && <Loader size="lg" backdrop vertical />}
      <FullCalendar
        ref={calendarRef}
        plugins={[
          dayGridPlugin,
          bootstrapPlugin,
          interactionPlugin,
          timeGridPlugin,
        ]}
        themeSystem="bootstrap"
        initialView="dayGridMonth"
        customButtons={{
          addAppointment: {
            text: "+",
            click: () => {
              history.push({
                pathname: AppRouteUi.Appointment.Add(),
                state: { from: AppRouteUi.Dashboard.Root() },
              });
            },
            hint: "Add appointment",
          },
        }}
        headerToolbar={{
          left: !userRole?.match("SuperAdmin") ? "addAppointment" : "",
          center: "prev,title,next",
          right: "dayGridMonth,dayGridWeek,timeGridDay",
        }}
        selectable={true}
        events={events}
        displayEventEnd={true}
        eventTimeFormat={{
          hour: "numeric",
          minute: "2-digit",
          meridiem: "short",
        }}
        windowResize={() => {
          const calendarApi = calendarRef.current?.getApi();
          if (!calendarApi) {
            return;
          }
          console.debug(isMobile, calendarApi.view.type);
          if (isMobile && calendarApi.view.type !== "listMonth") {
            calendarApi.changeView("listMonth");
            return;
          }
        }}
        eventContent={(arg) => {
          const metaData = arg.event.extendedProps as IEventData & {
            remainingCount?: number;
            isLastEvent?: boolean;
          };
          const calendarApi = calendarRef.current?.getApi();
          const isListView = calendarApi?.view.type === "listMonth";

          const content =
            !!arg.event.start && !!arg.event.end ? (
              isListView ? (
                <ListViewEvent event={arg.event} metaData={metaData} />
              ) : (
                <>
                  <MonthlyViewEvent event={arg.event} metaData={metaData} />
                  {metaData.isLastEvent && metaData.remainingCount && (
                    <small className="ms-1 text-muted">
                      (+{metaData.remainingCount} more)
                    </small>
                  )}
                </>
              )
            ) : !!arg.event.start ? (
              <small className="py-1 text-wrap text-dark">
                {arg.event.start.toLocaleTimeString() +
                  " - " +
                  arg.event.start.toLocaleTimeString()}
              </small>
            ) : (
              "Appointment"
            );

          return content;
        }}
        eventDisplay="block"
        eventBackgroundColor="#00BDB415"
        eventBorderColor="#00BDB4"
        eventTextColor="black"
        displayEventTime={false}
        dateClick={(d: DateClickArg) => {
          if (
            data?.filter(
              (appointment) =>
                UtilityDate.formatDate(appointment.appointmentStartDate) ===
                UtilityDate.formatDate(d.date)
            ).length
          )
            dispatch(ActionAppointmentCalendar.setSelectedDate(d.dateStr));
        }}
        moreLinkClick={(info) => {
          dispatch(
            ActionAppointmentCalendar.setSelectedDate(info.date.toISOString())
          );
        }}
        eventClick={(arg: EventClickArg) => {
          const date =
            arg.event.start?.toLocaleString("en-US") ||
            new Date().toLocaleString("en-US");
          setSelectedTask({
            appointmentId: arg.event.extendedProps.appointmentId,
            date,
            start: arg.event.start?.toLocaleString("en-US") || "",
            end: arg.event.end?.toLocaleString("en-US") || "",
          });
          toggleCheckInOutModal();
          dispatch(ActionAppointmentCalendar.setSelectedDate(date));
        }}
        datesSet={(payload) => {
          setCurrentView(payload.view.type);
          dispatch(
            ActionAppointmentCalendar.setMonth({
              start: new Date(payload.startStr).toISOString(),
              end: new Date(payload.endStr).toISOString(),
            })
          );
        }}
        dayCellClassNames="day-cell"
        eventClassNames={"click text-wrap"}
        moreLinkClassNames={"text-green-200"}
        slotEventOverlap={false}
        eventMaxStack={3}
      />
      {showCheckInOutModal && (
        <ModalEditCheckInCheckOut
          show={showCheckInOutModal}
          toggleModal={toggleCheckInOutModal}
          onSave={updateCheckInOut}
          selectedTask={selectedTask}
          setSelectedTask={setSelectedTask}
        />
      )}
    </div>
  );
};
